import Image from "next/image";
import Link from "next/link";
import { DropReel } from "@/components/drop-reel";
import { PillBadge } from "@/components/ui/pill-badge";
import { SoldOutBadge } from "@/components/ui/sold-out-badge";
import { resolveImageUrl } from "@/lib/product-images";
import type { Line } from "@/lib/schemas/lines";
import type { ProductListItem } from "@/lib/schemas/products";

type StyleProductCardProps = {
	product: ProductListItem;
	// Full line — enables the palette-aware DropReel placeholder when the
	// product has no mockup yet. Optional: on the cross-line collections grid
	// a product's line may not resolve, in which case we fall back to a text
	// placeholder.
	line?: Line;
	// Mono "LINE 0X · NAME" label shown above the title. Omitted on single-line
	// surfaces (the Styles PLP) where every card shares one line; supplied on
	// the cross-line Collections grid so each card carries its line.
	lineLabel?: string;
};

function formatPrice(cents: number): string {
	return `$${(cents / 100).toFixed(2)}`;
}

// Max colorway swatches rendered inline before collapsing to a "+N" count.
const MAX_SWATCHES = 5;

// Dense merch-grid card. The whole card links to the PDP; an "Add to bag" bar
// slides up over the image on hover (pure CSS group-hover — size selection
// happens on the PDP, so the bar is an affordance, not a real add). Colorway
// swatches below preview the available variants.
export function StyleProductCard({
	product,
	line,
	lineLabel,
}: StyleProductCardProps) {
	const imageUrl = resolveImageUrl(product.heroImageUrl);
	const colors = product.variantSummary.colors;
	const shown = colors.slice(0, MAX_SWATCHES);
	const overflow = colors.length - shown.length;
	// Inventory capacity (docs/architecture/inventory-capacity.md): absent capacityStatus means
	// unmanaged (no treatment); "sold_out" dims the image and swaps the
	// hover "Add to bag" bar for a badge. Coming-soon products get no
	// add-to-bag affordance either (the PDP has no buy box for them).
	const isSoldOut = product.capacityStatus === "sold_out";
	const purchasable = !isSoldOut && !product.isComingSoon;

	return (
		<Link
			href={`/products/${product.slug}`}
			className="group block text-ink no-underline"
		>
			<div className="relative overflow-hidden bg-[#efeeea]">
				{imageUrl ? (
					<div className="relative aspect-square">
						<Image
							src={imageUrl}
							alt={`${product.name} mockup`}
							fill
							sizes="(max-width: 680px) 50vw, (max-width: 1100px) 33vw, 20vw"
							className={`object-cover object-[50%_30%] ${isSoldOut ? "opacity-50 grayscale" : ""}`}
						/>
					</div>
				) : line ? (
					<DropReel
						line={line}
						product={product}
						height={300}
						showLabel={false}
					/>
				) : (
					<div className="flex aspect-square items-center justify-center">
						<span className="font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted">
							{product.line.name}
						</span>
					</div>
				)}
				{isSoldOut && (
					<div className="absolute top-2.5 left-2.5">
						<SoldOutBadge />
					</div>
				)}
				{(product.activeDrop ||
					product.upcomingDrop ||
					product.isComingSoon) && (
					// List chip (docs/architecture/drops.md). Three cases, most
					// urgent first: a product selling in a LIVE drop reads
					// "Limited drop", one in a scheduled drop reads "Limited drop
					// soon!", and a plain unreleased product reads "Coming Soon!".
					//
					// The live case matters most here. A drop product on a released
					// line turns up in search, on its line, and on category pages
					// like any other product, and without this chip nothing on
					// those surfaces says the run is limited. Detail pages carry
					// the dated countdown; lists keep the static copy.
					<div className="absolute top-2.5 right-2.5">
						<PillBadge
							label={
								product.activeDrop
									? "Limited drop"
									: product.upcomingDrop
										? "Limited drop soon!"
										: "Coming Soon!"
							}
							borderClassName={
								product.activeDrop || product.upcomingDrop
									? "border-accent bg-paper"
									: "border-[#d8d8d2] bg-paper"
							}
							dotClassName={
								product.activeDrop || product.upcomingDrop
									? "bg-accent"
									: "bg-muted"
							}
							textClassName={
								product.activeDrop || product.upcomingDrop
									? "text-accent"
									: "text-muted"
							}
						/>
					</div>
				)}
				{purchasable && (
					<div className="absolute inset-x-0 bottom-0 translate-y-full bg-ink py-3 text-center font-semibold text-body-sm text-paper tracking-[-0.02em] transition-transform duration-200 ease-out group-hover:translate-y-0">
						Add to bag
					</div>
				)}
			</div>

			<div className="mt-3">
				{lineLabel && (
					<p className="mb-1 font-mono text-mono-sm uppercase tracking-[var(--tracking-mono)] text-muted">
						{lineLabel}
					</p>
				)}
				<p className="font-semibold text-[0.9375rem] leading-tight tracking-[-0.02em]">
					{product.name}
				</p>
				<div className="mt-2 flex items-center justify-between">
					<span className="font-mono text-mono-md">
						{formatPrice(product.price)}
					</span>
					{shown.length > 0 && (
						<div className="flex items-center gap-1.5">
							{shown.map((color) => (
								<span
									key={color.name}
									title={color.name}
									className="block h-[13px] w-[13px] rounded-full border border-ink/20"
									style={{ backgroundColor: color.hex }}
								/>
							))}
							{overflow > 0 && (
								<span className="font-mono text-mono-sm text-muted">
									+{overflow}
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</Link>
	);
}
